import React, {memo, useEffect, useState, useContext} from 'react';
import { useSelector } from 'react-redux';
import {
  LineChart, Line, YAxis, Tooltip, Legend, ResponsiveContainer, Brush
} from 'recharts';
import styles from './ChartEmas.module.css'
import Spinner from '../components/Spinner';
import { CsrfTokenContext } from '../CsrfTokenContext';
import { isTokenExpired, handleLogin } from '../pages/utils/AuthenticationHelper';
    
const ChartEmas = memo(() => { 
// const ChartEmas = memo(({ data, ticker, ema1, ema2 }) => {

  const csrfToken = useContext(CsrfTokenContext);  

  const [data, setPloData] = useState([]);
  const [plotError, setPlotError] = useState('');  
  const [serieColorA, setSerieColorA] = useState('#8884d8');    
  const [serieColorB, setSerieColorB] = useState('#26ff00');    
  const [serieColorC, setSerieColorC] = useState('#ff7300');    
  const [previousSymbol, setPreviousSymbol] = useState('');    
  const [spin, setSpin] = useState(false); 
  const [showSpinner, setShowSpinner] = useState(false);   
  const { ema_1, ema_2, intervalSelected } = useSelector(state => state.emaSelection);
  const { selectedSymbol } = useSelector(state => state.streamCryptoInfo);

  useEffect(()=>{

    const refreshTokenIfNeeded = async () => {
      if (isTokenExpired()) {
        await handleLogin(csrfToken); // Refresh the token if expired
      }
      getPlotData();
    };

    const getPlotData = async () => {
      try { 
        
          if(ema_1 === 0 || ema_2 === 0 || intervalSelected === '' || selectedSymbol === '' || previousSymbol !== selectedSymbol){
            setPloData([]);
            if(previousSymbol !== selectedSymbol){
              setPreviousSymbol(selectedSymbol);
            }
            return;
          }
         
          setSpin(true);          

          setSerieColorA('white');
          setSerieColorB('white');
          setSerieColorC('white');
                                      
          const strFetch = `/api/plot_emas?symbol=${selectedSymbol}&interval=${intervalSelected}&ema_1=${ema_1}&ema_2=${ema_2}`;
                    
          const response = await fetch(strFetch, {
            method: 'GET',            
            headers: {
                'Content-Type': 'application/json',                
                'X-CSRFToken': csrfToken
            }
        });
          
          if (!response.ok) {              
              const errorData = await response.json();
              setPlotError(errorData.error);
              setSpin(false);
              throw new Error(errorData.error);
          }
          const result = await response.json();            
          setPloData(result);
          setSerieColorA('#8884d8');
          setSerieColorB('#26ff00');
          setSerieColorC('#ff7300');

          setSpin(false);

      } catch (err) {            
          console.error('Error plotting data:', err);
          setSpin(false);
          setSerieColorA('#8884d8');
          setSerieColorB('#26ff00');
          setSerieColorC('#ff7300');
      }        
    }

    refreshTokenIfNeeded();

  }, [ema_1, ema_2, intervalSelected, selectedSymbol, previousSymbol, csrfToken]);
  
  useEffect(() => {
    let timer;
    if (spin) {
      timer = setTimeout(() => {
        setShowSpinner(true);
      }, 500);
    } else {
      setShowSpinner(false);
    }

    return () => clearTimeout(timer);
  }, [spin]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className={styles.tooltip_paragraph} style={{textShadow:'-1px -1px 0 #131722, 1px -1px 0 #131722, -1px 1px 0 #131722, 1px 1px 0 #131722'}}>
          <div style={{ color: 'white' }}>
            {`Price: ${data.close.toFixed(7)}`}            
          </div>
          <div style={{ color: '#26ff00' }}>
            {`Ema_${ema_1}: ${data.ema_1.toFixed(7)}`}            
          </div>
          <div style={{ color: '#ff7300' }}>
            {`Ema_${ema_2}: ${data.ema_2.toFixed(7)}`}            
          </div>
          <div style={{ color: 'white' }}>
            {data.date_time}            
          </div>
        </div>
      );
    }    
  }  

  if(!data) return;

  const minY = Math.min(...data.map(d => Math.min(d.seriesA, d.seriesB, d.seriesC)));
  const maxY = Math.max(...data.map(d => Math.max(d.seriesA, d.seriesB, d.seriesC)));
  const adjustedMinY = minY - 10 < 0 ? 0 : minY - 10; // Ensure the minimum Y is not less than 0
  const adjustedMaxY = maxY + 10; // Adjust as needed  
  
  return (
    <>{showSpinner && <Spinner title="" color="#0e440d" /> }<>
    {data.length > 0 &&
      <div className={styles.centered_div}>
        <span className={styles.centered_text}>{selectedSymbol}</span>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <YAxis domain={[adjustedMinY, adjustedMaxY]} hide={true} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line type="linear" dataKey="close" stroke={serieColorA} fill={true} strokeWidth={2} dot={false} name='Price' />
            <Line type="linear" dataKey="ema_1" stroke={serieColorB} dot={false} name={`EMA_${ema_1}`} />
            <Line type="linear" dataKey="ema_2" stroke={serieColorC} dot={false} name={`EMA_${ema_2}`} />
            <Brush
              dataKey="date_time"
              height={20}
              stroke="#8884d8"
              fill="#f5f5f500"
              travellerWidth={10}
              gap={5} />
          </LineChart>
        </ResponsiveContainer>
      </div>}{plotError && <><div className={styles.error}>{plotError.message}</div><br /><br /></>}</></>
  );
});

export default ChartEmas;